import { useState } from 'react';
import { Mail, Loader2, X, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { readFunctionError } from '../../lib/email';
import { errorMessage } from '../../lib/errors';

interface RefundRequestModalProps {
    isOpen: boolean;
    onClose: () => void;
    userProfile: {
        email: string;
        full_name: string;
        org_id: string;
        tier: string;
    };
}

export const RefundRequestModal = ({
    isOpen,
    onClose,
    userProfile
}: RefundRequestModalProps) => {
    const [reason, setReason] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSending(true);
        setError(null);

        try {
            // Send the refund request to billing@ through the contact-form
            // function, whose fixed topic map picks the inbox. It used to go
            // through send-customer-email, whose G-04 check only allows the
            // org's own customers and members, so every request was refused.
            const { data, error: sendError } = await supabase.functions.invoke('contact-form', {
                body: {
                    name: userProfile.full_name || userProfile.email,
                    email: userProfile.email,
                    subject: 'refund',
                    message: `
REFUND REQUEST DETAILS
----------------------
Customer: ${userProfile.full_name}
Email: ${userProfile.email}
Organization ID: ${userProfile.org_id}
Current Tier: ${userProfile.tier}

Reason for request:
${reason || 'No reason provided.'}

--
Sent from ForgeDash Dashboard
                    `.trim(),
                }
            });

            if (sendError) throw await readFunctionError(sendError, 'Failed to send request. Please try again or email billing@forgedash.uk directly.');
            if (data?.error) throw new Error(data.error);

            setSuccess(true);
            setTimeout(() => {
                onClose();
                setSuccess(false);
                setReason('');
            }, 3000);
        } catch (err) {
            console.error('Refund request error:', err);
            setError(errorMessage(err) || 'Failed to send request. Please try again or email billing@forgedash.uk directly.');
        } finally {
            setSending(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-scrim/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-canvas border border-line rounded-[var(--fds-radius)] w-full max-w-lg shadow-2xl animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-line">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-warning" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-fg">Request Subscription Refund</h2>
                            <p className="text-xs text-fg-2">Available for 30 days after purchase</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-raised rounded-full transition-colors">
                        <X className="w-5 h-5 text-fg-2" />
                    </button>
                </div>

                {success ? (
                    <div className="p-12 text-center space-y-4">
                        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto text-success">
                            <Loader2 className="w-8 h-8 animate-spin" />
                        </div>
                        <h3 className="text-xl font-bold text-fg">Request Sent</h3>
                        <p className="text-fg-2">We've received your request. Our support team will review it and get back to you within 2-3 business days.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="p-6 space-y-6">
                        {error && (
                            <div className="p-4 bg-danger/10 border border-danger/20 rounded-[var(--fds-radius)] flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-danger shrink-0 mt-0.5" />
                                <p className="text-sm text-danger">{error}</p>
                            </div>
                        )}

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-fg-2 uppercase mb-1">Full Name</label>
                                    <div className="p-2.5 bg-raised border border-line rounded-[var(--fds-radius)] text-sm text-fg opacity-70">
                                        {userProfile.full_name}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-fg-2 uppercase mb-1">Email</label>
                                    <div className="p-2.5 bg-raised border border-line rounded-[var(--fds-radius)] text-sm text-fg opacity-70">
                                        {userProfile.email}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-fg-2 uppercase mb-1">
                                    Reason for refund (Optional)
                                </label>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    maxLength={4000}
                                    placeholder="Tell us why you're requesting a refund so we can improve ForgeDash..."
                                    className="w-full bg-raised border border-line rounded-[var(--fds-radius)] px-4 py-3 text-sm text-fg focus:ring-2 focus:ring-warning/50 outline-none min-h-[120px] transition-all resize-none"
                                />
                            </div>
                        </div>

                        <div className="bg-info/5 border border-info/20 rounded-[var(--fds-radius)] p-4 flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-info shrink-0 mt-0.5" />
                            <p className="text-xs text-fg-2 leading-relaxed">
                                Note: This request will be reviewed manually. If approved, your account will be downgraded to the Free tier immediately and your data will be preserved.
                            </p>
                        </div>

                        <div className="flex items-center gap-3 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-4 py-2.5 border border-line rounded-[var(--fds-radius)] text-sm font-medium text-fg-2 hover:bg-raised transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={sending}
                                className="flex-[2] px-4 py-2.5 bg-warning-fill text-on-fill rounded-[var(--fds-radius)] text-sm font-bold hover:bg-warning-fill transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                                {sending ? 'Sending...' : 'Send Refund Request'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};
